import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { Modal, Switch, Button, Checkbox } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { breadcrumbsActions } from "@store/breadcrumbs";
import { addProduct, deleteProduct, fetchAssets, updateProduct } from "@store/sku-management";
import { setLoading } from "@store/asset-loader";
import { showErrorMessage, showSuccessMessage } from "@store/dashboard";

import ApplicationPageTemplate from "../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate";
import { CountryList } from "../../../../components/CountryList";
import AddSkuModal from "./updateSkuDetails";

import TagComponent from "src/components/Tag";

import styled from "styled-components";
import ObjectView from "src/components/ObjectView";
import { SUPPLIER_TYPE } from "./constants";

const useStyles = makeStyles((theme) => ({
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "scroll",
    },
    modalContent: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: "#fff",
        padding: 16,
        width: "50%",
        overflow: "scroll",
    },
    modalTitle: {
        marginBottom: 10,
    },
    formLabel: {
        display: "in-line",
        marginBottom: 5,
        fontWeight: "bold",
    },
    formInput: {
        width: "100%",
        marginBottom: 10,
        padding: 8,
        border: "1px solid #ccc",
        borderRadius: 4,
        boxSizing: "border-box",
    },
    formCheckbox: {
        marginRight: 8,
    },
    formActions: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: 10,
    },
}));

const FilterActionContainer = styled.div`
    padding: 16px 16px 0 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const FilterContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const OptionContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const SwitchContainer = styled.div`
    background: rgb(255, 255, 255);
    padding: 5px 14px 5px 5px;
    margin-left: 10px;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-size: 12px;
    border: 1px solid rgb(196 196 196);
    display: flex;
    flex-wrap: nowrap;
    align-content: center;
    align-items: center;
    border-radius: 4px;
`;

const TitleCell = styled.div`
    min-width: 100%;
    flex: 1;
    font-weight: 500;
`;
const IDCell = styled.p`
    font-weight: 400;
`;

const TableProductLabels = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    padding: 8px 0;
`;

const ActionButtons = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    padding: 8px 0;
    gap: 10px;
`;

const Sku = () => {
    const dispatch = useDispatch();
    const classes = useStyles();

    const productList = useSelector((state) => state.skuList.productsList);
    const labels = useSelector((state) => state.skuList.labels);

    // const voucherTypes = useSelector((state) => state.skuList.voucherTypes);

    const [rowsDataGrid, setRowsDataGrid] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState("");
    const [defaultCountry, setDefaultCountry] = useState("my");
    const [openModal, setOpenModal] = useState(false);
    const [existingFormData, setExistingFormData] = useState(null);
    const [showDeleted, setShowDeleted] = useState(false);

    // const [airasiaVoucherTypeId, setAirasiaVoucherTypeId] = useState(null);
    const [showAirasia, setShowAirasia] = useState(false);

    const handleOpenModal = (imageUrl) => {
        setSelectedImageUrl(imageUrl);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectedImageUrl("");
        setModalOpen(false);
    };

    useEffect(() => {
        dispatch(
            breadcrumbsActions.setBreadcrumbs([
                { label: "Home", url: "/" },
                { label: "Gift SKU Management", url: "/gift-sku-management" },
            ])
        );
        dispatch(fetchAssets({ inactive: showDeleted }));
    }, [dispatch, showDeleted]);

    // useEffect(() => {
    //  if (voucherTypes && voucherTypes.length > 0) {
    //      const airasiaVoucher = voucherTypes.find((voucherType) =>
    //          voucherType?.title.toLowerCase().includes('airasia')
    //      );
    //      const id = airasiaVoucher ? airasiaVoucher.id : voucherTypes[0].id;

    //      setAirasiaVoucherTypeId(id);
    //  }
    // }, [voucherTypes]);

    useEffect(() => {
        if (productList && productList.length > 0) {
            setRowsDataGrid(showAirasia ? [...productList].filter((product) => product.supplier === SUPPLIER_TYPE.airasia) : [...productList]);
        }
    }, [productList, showAirasia]);

    const columns = [
        {
            field: "imageUrl",
            headerName: "Image",
            headerAlign: "center",
            width: 100,
            height: 100,
            renderCell: (params) => (
                <img
                    src={params.value}
                    alt="background"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    key={params.id}
                    onClick={() => handleOpenModal(params.value)}
                    onError={({ currentTarget }) => {
                        currentTarget.onerror = null; // prevents looping
                        currentTarget.src = "https://via.placeholder.com/300x300";
                    }}
                />
            ),
        },
        {
            field: "title",
            headerName: "Title",
            width: 250,
            renderCell: (params) => (
                <div>
                    <TitleCell>{params.value}</TitleCell>
                    <IDCell>{params.row.id}</IDCell>
                </div>
            ),
        },
        { field: "price", headerName: "Sale Price", headerAlign: "center", width: 30 },
        { field: "consumerPrice", headerName: "Original Price", headerAlign: "center", width: 30 },
        { field: 'id', headerName: 'ID', width: 90, hide: true },
        // { field: 'country', headerName: 'Country', width: 30 },
        {
            field: "airAsiaData",
            headerAlign: "center",
            headerName: "AA Product",
            width: 300,
            renderCell: (params) => <ObjectView data={params.value} />,
        },
        {
            field: "merchant",
            headerName: "Merchant",
            headerAlign: "center",
            width: 120,
            valueFormatter: ({ value }) => value?.merchantName || "",
        },
        {
            field: "category",
            headerName: "Category",
            headerAlign: "center",
            width: 150,
            valueGetter: getCategoryTitle,
        },
        {
            field: "labels",
            headerAlign: "center",
            headerName: "Labels",
            width: 200,
            renderCell: (params) => <TableProductLabels>{params.row.labels && params.row.labels.length > 0 && params.row.labels.map((label) => <TagComponent containerMargin="0px" tag={label} showDeleteIcon={false} />)}</TableProductLabels>,
        },
        // {
        //  field: '----',
        //  headerName: 'Deep Link',
        //  width: 200,
        //  renderCell: (params) => (
        //      <button onClick={() => copyProductLink(params)}>Click To Copy</button>
        //  ),
        // },
        {
            field: "isDeleted",
            headerAlign: "center",
            headerName: "Active/Inactive",
            width: 120,
            renderCell: (params) => <Switch checked={!params.value} onChange={(event) => handleIsDeletedChange(params.row, event.target.checked, 'isDeleted')} key={params.id} />,
        },
        {
            field: "isEarnPointsEnabled",
            headerAlign: "center",
            headerName: "Earn Points",
            width: 120,
            renderCell: (params) => params.row.airAsiaData ? <Switch checked={params.value} onChange={(event) => handleIsDeletedChange(params.row, event.target.checked, 'isEarnPointsEnabled')} key={params.id} /> : <></>,
        },
        {
            field: "isBurnPointsEnabled",
            headerAlign: "center",
            headerName: "Burn Points",
            width: 120,
            renderCell: (params) => params.row.airAsiaData ? <Switch checked={params.value} onChange={(event) => handleIsDeletedChange(params.row, event.target.checked, 'isBurnPointsEnabled')} key={params.id} /> : <></>,
        },

        {
            field: "actions",
            headerAlign: "center",
            headerName: "Actions",
            width: 200,
            renderCell: (params) => (
                <ActionButtons>
                    {SUPPLIER_TYPE.airasia === params.row.supplier && (
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<ModeEditIcon size="small" />}
                            onClick={() => {
                                // save changes for this row
                                const updatedRowData = { ...params.row };
                                setExistingFormData(updatedRowData);
                                setOpenModal(true);
                            }}
                        >
                            Update
                        </Button>
                    )}
                    <Button variant="outlined" color="primary" size="small" startIcon={<ContentCopyIcon size="small" />} onClick={() => copyProductLink(params)}>
                        Copy Deeplink
                    </Button>
                    {/* <button onClick={() => copyProductLink(params)}>Click To Copy</button> */}
                </ActionButtons>
            ),
        },
    ];

    function getCategoryTitle(params) {
        const categories = params.row.categories;
        if (categories && categories.length > 0) {
            return categories.map((category) => category.title).join(", ");
        }
        return "";
    }

    function copyProductLink(params) {
        const productLink = "airasia://gift/product?id=" + params.row.id;
        navigator.clipboard.writeText(productLink);
    }

    const handleOpenModaladd = () => {
        setOpenModal(true);
    };

    const handleCloseModaladd = () => {
        setExistingFormData(null);
        setOpenModal(false);
    };

    const handlSelectChange = (country) => {
        if (!country) return;
        setDefaultCountry(country);
        dispatch(fetchAssets({ id: country }));
    };

    const inputProps = {
        required: true,
        fullWidth: true,
        width: "250px",
        margin: "normal",
        onChange: handlSelectChange,
    };
    const formatSubErrors = (subErrors) => `Error while trying to add a new Product:\n${subErrors.map((item) => `${item.field}: ${item.message}`).join("\n")}`;

    const handleAddNew = (data) => {
        dispatch(setLoading(true));

        // Update Existing Product
        if (!!data.id) {
            dispatch(updateProduct({ ...data }))
                .then((response) => {
                    dispatch(setLoading(false));
                    if (response.payload.status !== "BAD_REQUEST") {
                        return dispatch(showSuccessMessage("Product Updated successfully"));
                    } else {
                        if (response.payload.subErrors !== null) {
                            return dispatch(showErrorMessage(formatSubErrors(response.payload.subErrors)));
                        } else {
                            return dispatch(showErrorMessage("error While Trying to add new Product.PLease check with Tech"));
                        }
                    }
                })
                .catch(function (error) {
                    console.log(error);
                    dispatch(setLoading(false));
                    dispatch(showErrorMessage(`Failed to update Product: ${error}`));
                });
        } else {
            // Add new Product
            dispatch(addProduct({ ...data }))
                .then((response) => {
                    dispatch(setLoading(false));
                    if (response.payload.status !== "BAD_REQUEST") {
                        return dispatch(showSuccessMessage("Product added successfully"));
                    } else {
                        let errorSummary = "";
                        if (response.payload.subErrors !== null) {
                            errorSummary = formatSubErrors(response.payload.subErrors);
                            return dispatch(showErrorMessage(errorSummary));
                        } else {
                            return dispatch(showErrorMessage("error While Trying to add new Product.PLease check with Tech"));
                        }
                    }
                })
                .catch((error) => {
                    dispatch(setLoading(false));
                    dispatch(showErrorMessage(`Failed to add Product: ${error}`));
                });
        }
    };

    const handleIsDeletedChange = (data, checked, flag) => {
        dispatch(setLoading(true));

        dispatch(deleteProduct({ productId: data.id,  flag: flag}))
            .then(() => {
                dispatch(setLoading(false));
                return dispatch(showSuccessMessage("Product updated successfully"));
            })
            .catch(function (error) {
                console.log(error);
                dispatch(showErrorMessage(`Failed to update Product: ${error}`));
            });
    };

    const filterAirasiaProductsOnly = (checked) => {
        setShowAirasia(checked);
        setRowsDataGrid(checked ? [...productList].filter((product) => product.supplier === SUPPLIER_TYPE.airasia) : [...productList]);
    };

    return (
        <ApplicationPageTemplate title={"Gift SKU Management"} description={"Manage Gift SKU with Add, Update and Activate"} pageReady={true}>
            <div style={{ height: 800, width: "100%" }}>
                <FilterActionContainer>
                    <FilterContainer>
                        <CountryList {...inputProps} onChange={handlSelectChange} placeholder="Select Country" id="country" value={defaultCountry != null ? defaultCountry : "my"} background="white" compactView />
                        <OptionContainer>
                            <SwitchContainer>
                                <Checkbox onChange={(event) => setShowDeleted(event.target.checked)} sx={{ "& .MuiSvgIcon-root": { fontSize: 13 } }} />
                                <span>Show Inactive</span>
                            </SwitchContainer>
                            <SwitchContainer>
                                <Checkbox onChange={(event) => filterAirasiaProductsOnly(event.target.checked)} size="small" sx={{ "& .MuiSvgIcon-root": { fontSize: 13 } }} />
                                <span>Show Airasia</span>
                            </SwitchContainer>
                        </OptionContainer>
                    </FilterContainer>
                    <div>
                        <Button variant="contained" color="primary" onClick={handleOpenModaladd} size="large" startIcon={<ControlPointIcon size="large" />} disableElevation>
                            Add New
                        </Button>
                    </div>
                </FilterActionContainer>
                <DataGrid getRowHeight={() => "auto"} sx={{ m: 2 }} groupBy={"country"} rows={rowsDataGrid} rowClassName="fixed-height-cell" rowHeight={90} columns={columns} pageSize={50} />
                <AddSkuModal isVisible={openModal} onClose={handleCloseModaladd} onCreate={handleAddNew} existingFormData={existingFormData} labels={labels} country={defaultCountry != null ? defaultCountry : "my"} />
                <Modal className={classes.modal} open={modalOpen} onClose={handleCloseModal}>
                    <Box sx={{ width: 300 }}>
                        <img src={selectedImageUrl} alt="" style={{ maxHeight: "50%", maxWidth: "100%" }} />
                    </Box>
                </Modal>
            </div>
        </ApplicationPageTemplate>
    );
};

export default Sku;